import { Button, Flex } from '@chakra-ui/react';
import { Form, Formik, useField } from 'formik';
import React from 'react';

import { TextUnderline } from '@/components';
import { GeneralSection, SectionType } from '@/layout';
import { createLog, t, useStateL } from '@/utils';

import PrivacyPolicyModal from './privacy-policy-modal';

import type { MouseEventHandler, PropsWithChildren } from 'react';

const log = createLog(`ContactSection`);

interface ThemedTextInputProps {
  name: string;
  type: string;

  // OPTIONAL
  id?: string;
  label?: string;
  placeholder?: string;
  required?: boolean;
}

const ThemedTextInput = ({
  name,
  type,

  // OPTIONAL
  id,
  label,
  placeholder,
  required = false,
}: ThemedTextInputProps) => {
  const props = { name, type, id, placeholder, required };

  // useField() returns [formik.getFieldProps(), formik.getFieldMeta()]
  // which we can spread on <input> and alse replace ErrorMessage entirely.
  const [field, meta] = useField(props);

  return (
    <>
      <label htmlFor={props.id || props.name}>{label}</label>
      <input className="textInput" {...field} {...props} />
      {meta.touched && meta.error ? <div className="error">{meta.error}</div> : null}
    </>
  );
};

interface ThemedTextareaProps {
  name: string;

  // OPTIONAL
  id?: string;
  label?: string;
  placeholder?: string;
  required?: boolean;
  rows?: number;
}

const ThemedTextarea = ({
  name,

  // OPTIONAL
  id,
  label,
  placeholder,
  required = false,
  rows,
}: ThemedTextareaProps) => {
  const props = { name, id, placeholder, required, rows };

  // useField() returns [formik.getFieldProps(), formik.getFieldMeta()]
  // which we can spread on <input> and alse replace ErrorMessage entirely.
  const [field, meta] = useField(props);

  return (
    <>
      <label className="textAreaLabel" htmlFor={props.id || props.name}>
        {label}
      </label>
      <textarea className="textArea" {...field} {...props} />
      {meta.touched && meta.error ? <div className="error">{meta.error}</div> : null}
    </>
  );
};

interface ThemedCheckboxProps {
  name: string;
  checked: boolean;
  onClick: MouseEventHandler<HTMLInputElement>;

  // OPTIONAL
  required?: boolean;
}

const ThemedCheckbox = ({
  children,
  name,
  checked,
  onClick,

  // OPTIONAL
  required = false,
}: PropsWithChildren<ThemedCheckboxProps>) => {
  const props = { name, checked, onClick, required };
  const [field, meta] = useField({ ...props, type: `checkbox` });

  return (
    <>
      <label className="checkbox">
        <input {...field} {...props} type="checkbox" />
        {children}
      </label>
      {meta.touched && meta.error ? <div className="error">{meta.error}</div> : null}
    </>
  );
};

// const ThemedSelect = ({label, ...props}) => {
//   // useField() returns [formik.getFieldProps(), formik.getFieldMeta()]
//   // which we can spread on <input> and alse replace ErrorMessage entirely.
//   const [field, meta] = useField(props);
//   return (
//     <>
//       <label className='selectLabel' htmlFor={props.id || props.name}>
//         {label}
//       </label>
//       <select className='select' {...field} {...props} />
//       {meta.touched && meta.error ? (
//         <div className='error'>{meta.error}</div>
//       ) : null}
//     </>
//   );
// };

// And now we can use these
const ContactForm = () => {
  const [modalShow, setModalShow] = useStateL(false, `modalShow`);
  const [acceptTerms, setAcceptTerms] = useStateL(false, `acceptTerms`);

  const handleClickAcceptTerms = () => {
    log.debug(`TERMS`);
    if (acceptTerms) {
      setAcceptTerms(false);
    } else {
      setAcceptTerms(true);
      // setModalShow(true);
    }
  };

  return (
    <>
      <Formik
        initialValues={{
          corpName: ``,
          name: ``,
          email: ``,
          acceptTerms: false,
        }}
        validateOnBlur={false}
        validateOnChange={false}
        onSubmit={async (values, { setSubmitting, resetForm /* , validate */ }) => {
          log.info(`Submit ContactForm: ${JSON.stringify(values, null, 2)}`);
          setSubmitting(true);
          try {
            await fetch(`/api/mail`, {
              method: `POST`,
              headers: {
                'Accept': `application/json, text/plain, */*`,
                'Content-Type': `application/json`,
              },
              body: JSON.stringify(values),
            });
            alert(`문의사항이 접수되었습니다.`);
            log.info(`ContactForm submitted successfully.`);
          } catch (error) {
            alert(`오류가 발생했습니다.`);
            log.error(`Error occurred while submitting ContactForm.`);
            log.error(error);
          } finally {
            setSubmitting(false);
            resetForm();
          }
        }}
      >
        <Form className="form">
          <Flex
            className="formRow"
            align="flex-start"
            flexDir={{ base: `column`, md: `row` }}
            justify="space-around"
          >
            {/* prettier-ignore */}
            <Flex className="formCol" maxW="100%" minW={{ md: `21rem` }}>
              <ThemedTextInput placeholder="업체명" name="corpName" type="text" required />
              <ThemedTextInput placeholder="연락처" name="phoneNum" type="text" required />
              <ThemedTextInput placeholder="홈페이지 주소" name="homepage" type="text" required />
            </Flex>
            {/* prettier-ignore */}
            <Flex className="formCol" maxW="100%" minW={{ md: `21rem` }}>
              <ThemedTextInput placeholder="담당자명" name="name" type="text" required />
              <ThemedTextInput placeholder="이메일" name="email" type="email" required />
              <ThemedTextInput placeholder="광고예산" name="budget" type="text" required />
            </Flex>
            <Flex className="formCol" maxW="100%" minW={{ md: `21rem` }}>
              <ThemedTextarea name="message" label="문의내용" rows={4} required />
              <ThemedCheckbox
                name="acceptTerms"
                checked={acceptTerms}
                onClick={() => handleClickAcceptTerms()}
                required
              >
                <span>
                  <TextUnderline
                    colorLight="white"
                    underlineColorLight="gray.500"
                    underlineHeight="20%"
                  >
                    <Button
                      className="link"
                      borderBottom="1px solid"
                      onClick={() => setModalShow(true)}
                      variant="link"
                    >
                      개인정보 처리방침
                    </Button>
                  </TextUnderline>
                  에 동의합니다
                </span>
              </ThemedCheckbox>
            </Flex>
          </Flex>
          <div className="buttonWrap">
            <button type="submit">문의 등록</button>
          </div>
        </Form>
      </Formik>
      <PrivacyPolicyModal
        handleAccept={() => {
          setModalShow(false);
          setAcceptTerms(true);
        }}
        handleHide={() => setModalShow(false)}
        show={modalShow}
      />
    </>
  );
};

const ContactSection = () => (
  <GeneralSection
    name="contact"
    sectionType={SectionType.DARK}
    title={t(`view.sections.contact.title`)}
    titleProps={{ fontSize: { base: `2.8rem`, md: `4rem` } }}
  >
    <ContactForm />
  </GeneralSection>
);

export default ContactSection;
